import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useCurrenciesQuery } from '~/queries/useCurrenciesQuery.ts';
import {
  GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM,
  guaranteesClientText,
} from '~/modules/guarantees/form/client/constants.ts';
import {
  buildGuaranteesClientApplicationPathByStatus,
  buildGuaranteesCounterpartyPath,
  isGuaranteesClientSigningStatus,
} from '~/modules/guarantees/utils.ts';
import {
  fetchGuaranteesApplication,
  fetchGuaranteesApplicationTenderInfo,
  fetchGuaranteesProzorroTenderById,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import { normalizeGuaranteesApplicationStatus } from '~/store/guarantees/utils.ts';
import type { AppDispatch } from '~/store/store.ts';
import { useGuaranteesApplicationPageState } from '../../hooks/useGuaranteesApplicationPageState.ts';
import { useGuaranteesApplicationRequest } from '../../hooks/useGuaranteesApplicationRequest.ts';
import {
  getGuaranteesRootTenderPublicId,
  buildGuaranteesCurrencyOptions,
  isGuaranteeOperationTypeProzorro,
  isGuaranteesClientApplicationReadOnly,
} from '../../rootSection/utils.ts';
import { useApplicationFormBootstrap } from '../../shared/hooks/useApplicationFormBootstrap.ts';
import { useInitialGuaranteesDictionaries } from '../../shared/hooks/useInitialGuaranteesDictionaries.ts';
import { useApplicationRequestFeedback } from '../../shared/hooks/useApplicationRequestFeedback.ts';
import { useClientApplicationActions } from './useClientApplicationActions.ts';
import { useClientApplicationTimeline } from './useClientApplicationTimeline.ts';
import type { TClientApplicationPageState } from './types.ts';
import { getDisabledClientStepKeysByStatus } from '../stepNavigation/utils.ts';

export const useClientApplicationPage = (): TClientApplicationPageState => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId = '' } = useParams();
  const routeContragentId =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM) ?? '';
  const getStartedPath = '/guarantees/get-started';
  const [isSecretSavedForCurrentApplication, setIsSecretSavedForCurrentApplication] =
    useState(false);
  const loadedProzorroTenderBootstrapKeyRef = useRef('');
  const isOpenedFromStepNavigator =
    (location.state as { fromGuaranteesClientStepNavigator?: boolean } | null)
      ?.fromGuaranteesClientStepNavigator === true;
  const {
    currencies,
    isLoading: isLoadingCurrencies,
    isError: isCurrenciesError,
    refetch: refetchCurrencies,
  } = useCurrenciesQuery();
  const guaranteeCurrencyOptions = useMemo(
    () => buildGuaranteesCurrencyOptions(currencies),
    [currencies]
  );

  const {
    application,
    applicationTimeline,
    applicationTenderInfo,
    principal,
    beneficiary,
    baseRelations,
    guaranteeForm,
    prozorroTender,
    mainDeals,
    mainDealsStatus,
    mainDealsRequestKey,
    accounts,
    principalDebitAccounts,
    securityCurrencies,
    requestError,
    isApplicationLoading,
    isApplicationTimelineLoading,
    isCheckingApplication,
    isSavingRootSection,
    isPrincipalLoading,
    isSavingPrincipal,
    isBeneficiaryLoading,
    isSavingBeneficiary,
    isBaseRelationsLoading,
    isSavingBaseRelations,
    isGuaranteeFormLoading,
    isSavingGuaranteeForm,
    isSearchingTender,
    isSelectingTender,
    isSelectingMainDeal,
    isSavingSecret,
    isLoadingMainDeals,
    isLoadingAccounts,
    isLoadingPrincipalDebitAccounts,
    isLoadingSecurityCurrencies,
  } = useGuaranteesApplicationPageState();
  const handleApplicationChange = useCallback(() => {
    setIsSecretSavedForCurrentApplication(false);
  }, []);
  const {
    applicationNumber,
    applicationStatusConfig,
    contragentId,
    currentApplicationVersion,
    currentRouteApplication,
    effectiveApplicationId,
    formDefaultValues,
    formMethods,
    isTemporaryApplicationNumber,
    primaryBeneficiary,
    savedGuaranteeOperationType,
    shouldShowDependentSections,
    shouldSkipNextAccountRestrictionCheckRef,
    storedAccountSelection,
    storedContractSelection,
  } = useApplicationFormBootstrap({
    accounts,
    application,
    applicationId,
    beneficiaries: beneficiary,
    guaranteeCurrencyOptions,
    principalDebitAccounts,
    securityCurrencies,
    isSecretSavedForCurrentApplication,
    routeContragentId,
    onApplicationChange: handleApplicationChange,
  });
  const normalizedApplicationStatus = normalizeGuaranteesApplicationStatus(
    currentRouteApplication?.applicationStatus
  );
  const isClientReadOnly = isGuaranteesClientApplicationReadOnly({
    applicationStatus: normalizedApplicationStatus,
    isAdministratorFlow: false,
  });
  const {
    hasInitialDictionariesError,
    isInitialDictionariesLoading,
    isRetryingInitialDictionaries,
    retryInitialDictionaries,
  } = useInitialGuaranteesDictionaries({
    contragentId,
    isCurrenciesError,
    isCurrenciesLoading: isLoadingCurrencies,
    mainDealsRequestKey,
    mainDealsStatus,
    refetchCurrencies,
  });
  const {
    modalAlert,
    modalSuccess,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
    handleCloseAlertModal,
    handleCloseSuccessModal,
  } = useApplicationRequestFeedback({
    applicationId,
    contragentId,
    dispatch,
  });
  const runGuaranteesApplicationRequest = useGuaranteesApplicationRequest({
    contragentId,
    applicationId: effectiveApplicationId,
    version: currentApplicationVersion,
    onError: showGuaranteesRequestError,
  });
  const navigateBackToApplications = useCallback(() => {
    if (!contragentId) {
      navigate(getStartedPath);

      return;
    }

    navigate(buildGuaranteesCounterpartyPath(contragentId, false));
  }, [contragentId, getStartedPath, navigate]);
  const actions = useClientApplicationActions({
    applicationId: effectiveApplicationId,
    contragentId,
    currentApplicationVersion,
    currentRouteApplication,
    formDefaultValues,
    getValues: formMethods.getValues,
    handleSubmit: formMethods.handleSubmit,
    isClientReadOnly,
    navigateBackToApplications,
    prozorroTender,
    runGuaranteesApplicationRequest,
    setIsSecretSavedForCurrentApplication,
    setModalAlert,
    setModalSuccess,
    shouldSkipNextAccountRestrictionCheckRef,
    showGuaranteesRequestError,
    storedAccountSelection,
  });

  useEffect(() => {
    if (!contragentId || !applicationId || applicationId === 'new') {
      return;
    }

    void dispatch(fetchGuaranteesApplication({ contragentId, applicationId }));
  }, [applicationId, contragentId, dispatch]);

  useEffect(() => {
    if (
      !contragentId ||
      !effectiveApplicationId ||
      effectiveApplicationId === 'new' ||
      !isGuaranteeOperationTypeProzorro(savedGuaranteeOperationType)
    ) {
      return;
    }

    const tenderPublicId = getGuaranteesRootTenderPublicId(currentRouteApplication);
    const bootstrapKey = `${effectiveApplicationId}:${tenderPublicId}`;

    if (loadedProzorroTenderBootstrapKeyRef.current === bootstrapKey) {
      return;
    }

    loadedProzorroTenderBootstrapKeyRef.current = bootstrapKey;

    void dispatch(
      fetchGuaranteesApplicationTenderInfo({
        contragentId,
        applicationId: effectiveApplicationId,
      })
    );

    if (tenderPublicId) {
      void dispatch(fetchGuaranteesProzorroTenderById({ tenderPublicId }));
    }
  }, [
    contragentId,
    currentRouteApplication,
    dispatch,
    effectiveApplicationId,
    savedGuaranteeOperationType,
  ]);

  useEffect(() => {
    if (requestError) {
      showGuaranteesRequestError(requestError);
    }
  }, [requestError, showGuaranteesRequestError]);

  useClientApplicationTimeline({
    effectiveApplicationId,
    isClientReadOnly,
  });

  const redirectPath = !contragentId
    ? getStartedPath
    : isGuaranteesClientSigningStatus(normalizedApplicationStatus) &&
        effectiveApplicationId &&
        !isOpenedFromStepNavigator
      ? buildGuaranteesClientApplicationPathByStatus(
          effectiveApplicationId,
          contragentId,
          normalizedApplicationStatus
        )
      : '';

  return {
    contragentId,
    effectiveApplicationId,
    formMethods,
    headerProps: {
      applicationNumber,
      applicationNumberTooltip: isTemporaryApplicationNumber
        ? guaranteesClientText.temporaryApplicationNumberTooltip
        : undefined,
      applicationNumberLabel: guaranteesClientText.applicationsNumberLabel,
      applicationIdLabel: guaranteesClientText.applicationIdLabel,
      backLabel: guaranteesClientText.backToApplicationsButton,
      contragentId,
      contragentIdLabel: guaranteesClientText.contragentIdLabel,
      effectiveApplicationId,
      onBack: navigateBackToApplications,
      statusConfig: applicationStatusConfig,
    },
    disabledClientStepKeys: getDisabledClientStepKeysByStatus(normalizedApplicationStatus),
    isApplicationLoading,
    hasInitialDictionariesError,
    isInitialDictionariesLoading,
    isRetryingInitialDictionaries,
    modalAlert,
    modalSuccess,
    onModalClose: handleCloseAlertModal,
    onSuccessModalClose: handleCloseSuccessModal,
    onRetryInitialDictionaries: () => void retryInitialDictionaries(),
    redirectPath,
    rootSectionProps: {
      application: currentRouteApplication,
      applicationTenderInfo,
      applicationId: effectiveApplicationId,
      storedContractSelection,
      accounts,
      principalDebitAccounts,
      securityCurrencies,
      mainDeals,
      principal,
      beneficiary: primaryBeneficiary,
      baseRelations,
      guaranteeForm,
      guaranteeCurrencyOptions,
      prozorroTender,
      isLoadingCurrencies,
      isLoadingSecurityCurrencies,
      isLoadingMainDeals,
      isLoadingAccounts,
      isLoadingPrincipalDebitAccounts,
      isLoadingPrincipal: isPrincipalLoading,
      isLoadingBeneficiary: isBeneficiaryLoading,
      isLoadingBaseRelations: isBaseRelationsLoading,
      isLoadingGuaranteeForm: isGuaranteeFormLoading,
      isSearchingTender,
      isSelectingTender,
      isSelectingMainDeal,
      isCheckingApplication,
      isSaving: isSavingRootSection,
      isSavingAccount: isSavingSecret,
      isSavingPrincipal,
      isSavingBeneficiary,
      isSavingBaseRelations,
      isSavingGuaranteeForm,
      isReadOnly: isClientReadOnly,
      requireChangesToEnableActions: true,
      shouldShowDependentSections,
      savedGuaranteeOperationType,
      onCheckApplication: actions.handleCheckApplicationRequest,
      onSubmit: actions.handleRootFormSubmit,
      onAccountsLoad: actions.handleLoadAccounts,
      onPrincipalDebitAccountsLoad: actions.handleLoadPrincipalDebitAccounts,
      onSecurityCurrenciesLoad: actions.handleLoadSecurityCurrencies,
      onSecretSubmit: actions.handleSaveSecret,
      onPrincipalLoad: actions.handleLoadPrincipal,
      onPrincipalSubmit: actions.handleSavePrincipal,
      onBeneficiaryLoad: actions.handleLoadBeneficiary,
      onBeneficiarySubmit: actions.handleSaveBeneficiary,
      onBaseRelationsLoad: actions.handleLoadBaseRelations,
      onBaseRelationsSubmit: actions.handleSaveBaseRelations,
      onGuaranteeFormLoad: actions.handleLoadGuaranteeForm,
      onGuaranteeFormSubmit: actions.handleSaveGuaranteeForm,
      onTenderSearch: actions.handleSearchTender,
      onTenderSelect: actions.handleSelectTender,
      onMainDealSelect: actions.handleMainDealSelect,
    },
    statusFlowProps: isClientReadOnly
      ? {
          events: applicationTimeline,
          isLoading: isApplicationTimelineLoading,
        }
      : null,
  };
};
